import { Contact, Deal } from '../types';

// // 👇 백엔드 API 주소 (라우터 구조에 맞춰 /api 추가)
// const API_BASE_URL = "http://localhost:8000/api";
// API 통합관리
import { API_BASE_URL } from '../config';

export const crmDataService = {

  // =================================================================
  // 📊 [SECTION 1] 대시보드 (Dashboard)
  // =================================================================
  // 설명: 2026년 목표, 달성률, 3개년 추이 등 차트와 KPI 카드에 들어갈 데이터
  // 백엔드(crm_data_service.py)에서 계산된 데이터를 가져온다.
  // -----------------------------------------------------------------

  /**
   * 1-1. [상단 핵심 지표] 2026년 KPI 가져오기
   * - 연간 목표 (Annual Target)
   * - 현재 달성률 (Achievement Rate)
   * - YoY 성장률 (Year-over-Year Trend)
   * - 전체 활동 수 (Activity Count)
   */
  getDashboardKPI: async (startDate?: string, endDate?: string, assignee?: string) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start', startDate);
      if (endDate) params.append('end', endDate);
      // 🚨 [추가] 담당자 필터링 파라미터 추가
      if (assignee && assignee !== '전체') params.append('assignee', assignee);


      const suffix = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${API_BASE_URL}/dashboard/summary${suffix}`);
      if (!response.ok) throw new Error('KPI 통신 오류');
      return await response.json();
    } catch (error) {
      console.error("❌ KPI 로드 실패:", error);
      return { annual_target: 0, total_forecast: 0, achievement_rate: 0, yoy_growth: 0, activity_count: 0 };
    }
  },

  /**
   * 1-2. [중단 차트] 그래프용 데이터 모음 가져오기
   * - 최근 3개년 매출 추이 (2023 ~ 2025) -> 막대/선 그래프용
   * - 2026년 월별 파이프라인 (예상 매출) -> 복합 차트용
   */
  getDashboardCharts: async (startDate?: string, endDate?: string, assignee?: string) => { // 🚨 [추가] assignee 파라미터
    try {
      const params = new URLSearchParams();
      if (assignee && assignee !== '전체') params.append('assignee', assignee);
      if (startDate) params.append('start', startDate);
      if (endDate) params.append('end', endDate);

      const suffix = params.toString() ? `?${params.toString()}` : '';

      
      const response = await fetch(`${API_BASE_URL}/dashboard/charts${suffix}`);
      if (!response.ok) throw new Error('차트 통신 오류');
      return await response.json();
    } catch (error) {
      console.error("❌ 차트 로드 실패:", error);
      return { history: [], pipeline: [] }; 
    }
  },

  /**
   * 1-3. [하단 차트] 팀별 매출 기여도 (신규 추가!)
   */
  getCustomerGradeDistribution: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/dashboard/distribution`);
      if (!response.ok) return [];
      return await response.json();
    } catch (error) {
      console.error("❌ 분포 차트 로드 실패:", error);
      return [];
    }
  },

  getDashboardActivityFeed: async (days: number = 7) => {
    try {
      // 🚨 방금 main.py에 만든 그 주소로 찌릅니다!
      const response = await fetch(`${API_BASE_URL}/activities/feed?days=${days}`);
      if (!response.ok) return [];
      return await response.json();
    } catch (error) {
      console.error("❌ 피드 로드 실패:", error);
      return [];
    }
  },


  // =================================================================
  // 👥 [SECTION 2] 고객 관리 (Customer Management)
  // =================================================================
  // 설명: 고객사(Contact) 리스트 조회, 상세 정보, 담당자 매핑
  // -----------------------------------------------------------------

  /**
   * 2-1. 전체 고객사 리스트 가져오기
   */
  getContacts: async (refresh: boolean = false): Promise<Contact[]> => {
    try {
      const suffix = refresh ? '?refresh=true' : '';
      
      // 🚨 [여기가 문제였습니다!] 주소를 백엔드랑 똑같이 맞췄습니다.
      const response = await fetch(`${API_BASE_URL}/contacts${suffix}`);
      
      if (!response.ok) throw new Error('고객사 목록 통신 오류');
      return await response.json();
    } catch (error) {
      console.error("❌ 고객사 데이터 로드 실패:", error);
      throw error; // 여기서 에러를 던져야 loadData에서 잡을 수 있습니다.
    }
  },

  saveContact: async (contact: Contact) => {
    try {
      const response = await fetch(`${API_BASE_URL}/contacts/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contact)
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.detail || json?.message || '고객 저장 실패');
      return json?.status === 'success';
    } catch (error) {
      console.error("❌ 저장 실패:", error);
      return false;
    }
  },

  deleteContact: async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/contacts/${encodeURIComponent(id)}`, {
        method: 'DELETE'
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.detail || json?.message || '고객 삭제 실패');
      return json?.status === 'success';
    } catch (error) {
      console.error("❌ 삭제 실패:", error);
      return false;
    }
  },

  uploadExcelFile: async (target: 'contacts' | 'forecast' | 'sales_target', file: File) => {
    try {
      const formData = new FormData();
      formData.append('target', target);
      formData.append('file', file);

      const response = await fetch(`${API_BASE_URL}/files/upload-excel`, {
        method: 'POST',
        body: formData
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.detail || json?.message || '업로드 실패');
      return json;
    } catch (error) {
      console.error("❌ 엑셀 업로드 실패:", error);
      return { status: "error", message: String(error) };
    }
  },

  finalizeExcelUpload: async (target: 'contacts' | 'forecast' | 'sales_target') => {
    try {
      const response = await fetch(`${API_BASE_URL}/files/finalize-upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target })
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.detail || json?.message || '최종 업로드 실패');
      return json;
    } catch (error) {
      console.error("❌ 최종 업로드 실패:", error);
      return { status: "error", message: String(error) };
    }
  },

  syncContactsToSheet: async (contacts: any[]) => {
    return true;
  },


  // =================================================================
  // 💼 [SECTION 3] 영업 기회 (Opportunity / Deal)
  // =================================================================
  // 설명: 진행 중인 영업 건(Deal) 리스트 및 상태 관리
  // -----------------------------------------------------------------

  /**
   * 3-1. 전체 영업 기회(Deal) 리스트 가져오기
   * - 백엔드의 2026년 매출 데이터를 가져와서 DealBoard에 넘겨줍니다.
   */
  getDeals: async (refresh: boolean = false): Promise<Deal[]> => {
    try {
      // 💡 [캐시 버스팅] 주소 끝에 현재 시간(밀리초)을 붙여 매번 새로운 주소인 것처럼 위장합니다.
      const timestamp = new Date().getTime();
      const suffix = refresh ? `?t=${timestamp}&refresh=true` : `?t=${timestamp}`;
      const response = await fetch(`${API_BASE_URL}/forecast/list${suffix}`);
      
      if (!response.ok) throw new Error('데이터 통신 오류');
      
      const data = await response.json();
      console.log(`✅ DealBoard 데이터 로드 완료: ${data.length}건`);
      return data; 
    } catch (error) {
      console.error("❌ Deal 데이터 로드 실패:", error);
      return [];
    }
  },

  /**
   * 3-2. [신규] 특정 영업 건의 활동 정보(메모/방문일) 가져오기 🚨
   * - 딜보드 카드를 클릭했을 때 DB에 저장된 활동 내역을 불러옵니다.
   */
  getDealActivity: async (dealId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/activity/${dealId}`);
      if (!response.ok) throw new Error('활동 정보 로드 오류');
      return await response.json();
    } catch (error) {
      console.error("❌ 활동 정보 로드 실패:", error);
      return null;
    }
  },

  /**
   * 3-3. [신규] 영업 활동 정보(메모/방문일) 저장하기 🚨
   * - 팝업창에서 작성한 내용을 DB에 저장합니다.
   */
  saveDealActivity: async (activityData: {
    deal_id: string;
    content?: string;
    last_meet?: string;
    next_plan?: string;
    memo?: string;
    activity_type?: string;
    last_editor?: string;
  }) => {
   try {
      // 🚨 [핵심] 백엔드가 content를 필수값으로 요구하므로, 
      // memo가 들어오면 content로 변환해주고, 둘 다 없으면 기본 문구라도 넣습니다.
      const payload = {
        ...activityData,
        content: activityData.content || activityData.memo || `${activityData.activity_type || '영업'} 단계로 이동`
      };

      const response = await fetch(`${API_BASE_URL}/activity/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload), // 👈 payload를 보냄
      });
      if (!response.ok) throw new Error('활동 정보 저장 오류');
      return await response.json();
    } catch (error) {
      console.error("❌ 활동 정보 저장 실패:", error);
      return { status: "error", message: String(error) };
    }
  },

  /**
   * 3-4 [신규] 전체 영업 활동 내역 가져오기
   */

  getAllDealActivities: async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/activities/all`);
        return await response.json();
    } catch (error) {
        console.error("❌ 전체 활동 로드 실패:", error);
        return {};
    }
},

/**
   * 3-5. [신규] 특정 딜의 AI 종합 분석 실행 (에이전트 가동)
   */
  runDeepAnalysis: async (dealId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/deals/${dealId}/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('분석 실패');
      return await response.json();
    } catch (error) {
      console.error("❌ 종합 분석 오류:", error);
      return null;
    }
  },


  // =================================================================
  // 💰 [SECTION 4] 매출 목표 (Sales Target)
  // =================================================================
  /**
   * 4-1. 매출 목표 리스트 가져오기
   */
  getSalesTargets: async (refresh: boolean = false) => {
    try {
      const suffix = refresh ? '?refresh=true' : '';
      const response = await fetch(`${API_BASE_URL}/sales-targets${suffix}`);
      const json = await response.json();
      // 백엔드 응답 구조: { status: "success", data: [...], all_total: number }
      return json;
    } catch (error) {
      console.error("❌ Sales Target Error:", error);
      return { status: "error", data: [], all_total: 0 };
    }
  },

  /**
   * 4-2. 매출 목표 수정하기
   */
  updateSalesTarget: async (id: number, months: number[]) => {
    try {
      const response = await fetch(`${API_BASE_URL}/sales-targets/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ months })
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.message || 'Update failed');
      return json;
    } catch (error) {
      console.error("❌ Sales Target Update Error:", error);
      return { status: "error", message: String(error) };
    }
  },

  /**
   * 4-3. 매출 목표 담당자 추가하기
   */
  addSalesTargetManager: async (manager: string, year: number = 2026) => {
    try {
      const response = await fetch(`${API_BASE_URL}/sales-targets/manager`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ manager, year })
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.detail || json?.message || 'Add manager failed');
      return json;
    } catch (error) {
      console.error("❌ Sales Target Manager Add Error:", error);
      return { status: "error", message: String(error) };
    }
  },

  /**
   * 4-4. 매출 목표 담당자 삭제하기
   */
  deleteSalesTargetManager: async (manager: string, year: number = 2026) => {
    try {
      const encoded = encodeURIComponent(manager);
      const response = await fetch(`${API_BASE_URL}/sales-targets/manager/${encoded}?year=${year}`, {
        method: 'DELETE'
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.detail || json?.message || 'Delete manager failed');
      return json;
    } catch (error) {
      console.error("❌ Sales Target Manager Delete Error:", error);
      return { status: "error", message: String(error) };
    }
  },




  // =================================================================
  // 💰 [SECTION 5] 매출 관리 (Sales Management)
  // =================================================================
  // 설명: 확정된 매출 건에 대한 관리 (세금계산서, 수금 등)
  // -----------------------------------------------------------------

  /**
   * 5-1. [NEW] 매출 예정 리스트 (엑셀 상세 데이터) 가져오기
   * - SalesForecast.tsx 화면에 뿌려줄 데이터
   */
  getSalesForecastList: async (startDate?: string, endDate?: string, refresh: boolean = false, type: string = "active") => {
    try {
      // 쿼리 파라미터 생성 (예: ?start=2026-01-01&end=2026-12-31)
      const params = new URLSearchParams();
      if (startDate) params.append('start', startDate);
      if (endDate) params.append('end', endDate);
      if (refresh) params.append('refresh', 'true');

      // 백엔드 메인에서 새로 만든 'type' 파라미터를 전달합니다.
      params.append('type', type);

      const suffix = params.toString() ? `?${params.toString()}` : '';
      
      // 📡 백엔드 API 호출
      const response = await fetch(`${API_BASE_URL}/forecast/list${suffix}`);
      if (!response.ok) throw new Error('Forecast 데이터 통신 오류');
      
      const data = await response.json();
      console.log(`✅ 매출 예정 데이터 로드 완료: ${data.length}건`);
      return data;
    } catch (error) {
      console.error("❌ 매출 예정 리스트 로드 실패:", error);
      return [];
    }
  },

  /**
   * 5-2. [기존 유지] 시트 동기화 (Mock)
   */
  syncForecastToSheet: async (deals: any[], contacts: any[]) => {
    console.log("📊 전체 시트 동기화 요청");
    return true; 
  },

  /**
   * 5-3. [기존 유지] 상세 내역 동기화 (Mock)
   */
  syncForecastDetailToSheet: async (data: any[]) => {
    console.log("📊 상세 내역 동기화 요청");
    return true; 
  },

  /**
   * 5-4. [수정] 매출 데이터 수정하기 (사유, 편집자 추가)
   */
  updateForecastItem: async (id: string, updates: any, reason: string, editorName: string = "") => {
    try {
      // 🌟 [핵심 해결 포인트] 프론트 화면의 변수명을 DB 컬럼명으로 '번역' 해줍니다.
      const mappedUpdates = { ...updates };
      
      if (mappedUpdates.poNo !== undefined) {
        mappedUpdates.po_no = mappedUpdates.poNo; 
        delete mappedUpdates.poNo; // 옛날 이름 지우기
      }
      if (mappedUpdates.projNo !== undefined) {
        mappedUpdates.proj_no = mappedUpdates.projNo;
        delete mappedUpdates.projNo;
      }
      if (mappedUpdates.건명 !== undefined) {
        mappedUpdates.project_name = mappedUpdates.건명;
        delete mappedUpdates.건명;
      }
      if (mappedUpdates.산업분야 !== undefined) {
        mappedUpdates.industry = mappedUpdates.산업분야;
        delete mappedUpdates.산업분야;
      }

      console.log(`📡 수정 요청 전송: ID=${id}`, mappedUpdates, reason);
      
      const response = await fetch(`${API_BASE_URL}/forecast/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        // 🚨 updates 대신 이름표를 바꾼 mappedUpdates를 보냅니다!
        body: JSON.stringify({ id, updates: mappedUpdates, reason, editor_name: editorName }), 
      });

      if (!response.ok) throw new Error('수정 실패');
      const result = await response.json();
      console.log("✅ 수정 완료:", result);
      return true;
    } catch (error) {
      console.error("❌ 수정 API 에러:", error);
      return false;
    }
  },

  /**
   * 5-5. [신규] 매출 내역 추가 (New!)
   * - SalesForecast.tsx의 모달에서 호출
   */
  addForecastItem: async (itemData: any) => {
    try {
      const response = await fetch(`${API_BASE_URL}/forecast/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(itemData),
      });
      const result = await response.json();
      return result.status === 'success';
    } catch (error) {
      console.error('❌ 매출 추가 에러:', error);
      return false;
    }
  },

  /**
   * 5-6. [신규] 매출 내역 삭제 (New!)
   * - SalesForecast.tsx의 쓰레기통 아이콘에서 호출
   */
  deleteForecastItem: async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/forecast/${id}`, {
        method: 'DELETE',
      });
      const result = await response.json();
      return result.status === 'success';
    } catch (error) {
      console.error('❌ 매출 삭제 에러:', error);
      return false;
    }
  },


  // =================================================================
  // 📝 [SECTION 6] 매출 세부 내역 (Forecast Detail)
  // =================================================================
  // 설명: 특정 Forecast 항목의 상세 품목, 마진율 등 깊은 정보
  // -----------------------------------------------------------------

  /**
   * 6-1. 특정 매출 건 상세 정보 조회 (이름 변경: Detail -> ForecastDetail)
   */
  getSalesForecastDetail: async (id: string) => {
    try {
      // 📡 백엔드 API 호출 (/api/forecast/{id})
      const response = await fetch(`${API_BASE_URL}/forecast/${id}`);
      if (!response.ok) throw new Error('상세 정보 통신 오류');
      return await response.json();
    } catch (error) {
      console.error("❌ 상세 정보 로드 실패:", error);
      return null;
    }
  },


  // =================================================================
  // 📜 [SECTION 7] 매출 이력 관리 (Sales History)
  // =================================================================
  // 설명: 자사 및 경쟁사의 과거 실적 데이터 조회 (이미 완료된 건)
  // -----------------------------------------------------------------

  /**
   * 7-1. 영업 이력 데이터 가져오기
   * - type: 'internal' (자사) 또는 'competitor' (경쟁사)
   */
  getSalesHistory: async (type: 'internal' | 'competitor') => {
    try {
      // 🚧 백엔드 연결 예정
      // const response = await fetch(`${API_BASE_URL}/history/${type}`);
      // return await response.json();
      return []; 
    } catch (error) {
      console.error("이력 데이터 로드 실패:", error);
      return [];
    }
  },


  // =================================================================
  // 🏃 [SECTION 8] 활동 로그 (Activity Logs)
  // =================================================================
  // 설명: 영업 사원들의 미팅, 전화, 이메일 등 활동 기록
  // -----------------------------------------------------------------

// =================================================================
  // 🏃 [SECTION 8] 활동 로그 (Activity Logs)
  // =================================================================

  /**
   * 8-1. 특정 영업기회(Deal)의 타임라인 히스토리 가져오기 (ActivityLog.tsx 용)
   * - dealId를 기준으로 해당 건의 상세 히스토리를 가져옵니다.
   */
  getActivityHistory: async (dealId: string) => {
    try {
      // 🚨 배포 환경 대응을 위해 API_BASE_URL 사용
      const response = await fetch(`${API_BASE_URL}/activity/${dealId}?t=${new Date().getTime()}`);
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error("히스토리 로드 실패:", error);
      return null;
    }
  },

  /**
   * 8-2. 전체 영업 피드 가져오기 (Dashboard, DealDetailModal 용)
   * - days가 0이면 전체, 그 외에는 최근 N일치 데이터를 가져옵니다.
   */
  getActivityFeed: async (days: number = 0) => {
    try {
      const response = await fetch(`${API_BASE_URL}/activities/feed?days=${days}&t=${new Date().getTime()}`);
      if (!response.ok) return [];
      return await response.json();
    } catch (error) {
      console.error('영업 피드 로드 실패:', error);
      return [];
    }
  },

  /**
   * 8-3. 특정 영업기회에 새로운 활동 내역(타임라인) 저장
   */
  updateSalesActivity: async (dealId: string, activityData: any) => {
    try {
      const response = await fetch(`${API_BASE_URL}/activity/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(activityData)
      });
      if (!response.ok) throw new Error('저장 실패');
      return await response.json();
    } catch (error) {
      console.error("활동 저장 실패:", error);
      throw error;
    }
  },

  // =================================================================
  // 🤝 [SECTION 9] 영업 지원 (Sales Support)
  // =================================================================
  // 설명: 제안서, 견적서, 기술 자료 등 영업에 필요한 파일 지원
  // -----------------------------------------------------------------

  /**
   * 9-1. 중앙 DB 폴더 바로가기 URL (단순 링크)
   */
  getCentralDbUrl: (type: 'specification' | 'proposal') => {
    const ROOT_ID = "1PaYlFIlBYoapnV3BflYdJAn3lPhLkvwm"; 
    return `https://drive.google.com/drive/folders/${ROOT_ID}`;
  },


  // =================================================================
  // 📑 [SECTION 10] 보고서 (Reports)
  // =================================================================
  // 설명: 주간/월간 보고서 자동 생성 및 조회
  // -----------------------------------------------------------------

  /**
   * 10-1. [신규] AI 지능형 보고서 생성 API 호출
   */
  generateReport: async (type: string, startDate: string, endDate: string, assignee: string) => {
    try {
      console.log(`📊 보고서 생성 요청: ${assignee} / ${startDate} ~ ${endDate}`);
      const response = await fetch(`${API_BASE_URL}/reports/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            type: type, 
            start_date: startDate, 
            end_date: endDate, 
            assignee: assignee 
        })
      });
      
      if (!response.ok) throw new Error('보고서 생성 실패');
      return await response.json();
    } catch (error) {
      console.error("❌ 보고서 생성 API 에러:", error);
      return { status: "error", report_content: "보고서 생성 중 오류가 발생했습니다. 백엔드 연결을 확인하세요." };
    }
  },

  /**
   * 10-3. [추가] 생성된 리포트 저장 (Google Docs 연동)
   * - Reports.tsx에서 'Google Docs 저장' 버튼 누를 때 사용
   */
  createAIReport: async (title: string, content: string) => {
    console.log(`📝 리포트 저장 요청: ${title}`);
    // 🚧 백엔드 연결 전에는 가짜 URL 반환 (성공한 척)
    return "https://docs.google.com/document/d/dummy-id";
  },



  // =================================================================
  // 🚀 [SECTION 11] 노션 연동 (Notion Integration)
  // =================================================================
  // 설명: 노션 API를 활용한 양방향 데이터 동기화 (CRM <-> Notion)
  // 백엔드의 NotionRouter와 통신합니다.
  // -----------------------------------------------------------------

  /**
   * 11-1. 노션 데이터 전체 동기화 (Sync)
   * - DealBoard.tsx의 'Notion 동기화' 버튼 클릭 시 호출
   * - 노션의 최신 변경사항을 가져와 DB를 업데이트하고, 결과를 반환함
   */
  syncWithNotion: async (): Promise<Deal[] | null> => {
    try {
      console.log("🔄 Notion 동기화 요청 전송...");
      
      // 📡 백엔드 API 호출: POST /api/notion/sync
      // (백엔드 구현 전까지는 빈 배열 반환하여 에러 방지)
      const response = await fetch(`${API_BASE_URL}/notion/sync`, {
        method: 'POST',
      });

      if (!response.ok) {
        console.warn("⚠️ Notion API 미연결 (Mock 데이터 사용)");
        return null; 
      }

      const syncedDeals = await response.json();
      console.log(`✅ Notion 동기화 완료: ${syncedDeals.length}건 갱신됨`);
      return syncedDeals;

    } catch (error) {
      console.error("❌ Notion 동기화 실패:", error);
      return null;
    }
  },

  /**
   * 11-2. 특정 영업 기회를 노션으로 내보내기 (Push)
   * - CRM에서 '영업 기회 추가' 시 호출하여 노션에도 페이지 생성
   */
  pushToNotion: async (deal: Deal) => {
    try {
      console.log(`📤 Notion으로 데이터 전송: ${deal.title}`);
      
      const response = await fetch(`${API_BASE_URL}/notion/push`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(deal)
      });

      if (!response.ok) throw new Error('Notion Push Failed');
      return true;
    } catch (error) {
      console.error("❌ Notion 전송 실패:", error);
      return false;
    }
  },


  // =================================================================
  // 🏢 [SECTION 12] 조직 관리 (Team Management)
  // =================================================================
  
  /**
   * [신규] DB에 저장된 활성 팀 목록 가져오기
   * - SalesForecast의 추가 모달에서 팀 드롭다운을 채울 때 사용
   */
  getActiveTeams: async () => {
    try {
      // 📡 백엔드 API 호출 (/api/admin/teams)
      const response = await fetch(`${API_BASE_URL}/admin/teams`);
      
      if (!response.ok) throw new Error('팀 목록 통신 오류');
      
      const data = await response.json();
      // 백엔드에서 {"teams": [...]} 형태로 줌
      return data.teams || []; 
    } catch (error) {
      console.error("❌ 팀 목록 로드 실패:", error);
      return [];
    }
  },


  /**
   * [신규] DB에 있는 '진짜' 유저 목록 가져오기 (최마케 같은 가짜 제거)
   */
  getAllUsers: async () => {
    try {
      // 📡 백엔드 API 호출 (/api/admin/users)
      const response = await fetch(`${API_BASE_URL}/admin/users`);
      
      if (!response.ok) throw new Error('유저 목록 통신 오류');
      
      const data = await response.json();
      // 백엔드에서 {"users": [...]} 형태로 줌
      return data.users || []; 
    } catch (error) {
      console.error("❌ 유저 목록 로드 실패:", error);
      return [];
    }
  },

  // =================================================================
  // 🏢 [SECTION 13] WBS 프로젝트 진행 상황 관리 (Project Progress Management)
  // =================================================================

  getProjectProgress: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/project-progress`);
      if (!response.ok) throw new Error('WBS 로드 실패');
      const json = await response.json();
      return json?.projects || [];
    } catch (error) {
      console.error("❌ WBS 데이터 로드 실패:", error);
      return [];
    }
  },

  saveProjectProgress: async (projects: any[]) => {
    try {
      const response = await fetch(`${API_BASE_URL}/project-progress`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projects }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.detail || 'WBS 저장 실패');
      return json?.status === 'success';
    } catch (error) {
      console.error("❌ WBS 저장 실패:", error);
      return false;
    }
  },

  getWbsTeamHistory: async (projectId: string, team: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/wbs/history?project_id=${projectId}&team=${team}`);
      if (!response.ok) return [];
      const json = await response.json();
      return json.history || [];
    } catch (error) {
      console.error("❌ WBS 히스토리 로드 실패:", error);
      return [];
    }
  },

  addWbsTeamHistory: async (projectId: string, team: string, content: string, editor: string = "관리자") => {
    try {
      const response = await fetch(`${API_BASE_URL}/wbs/history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId, team, content, editor }),
      });
      const json = await response.json();
      return json.status === 'success';
    } catch (error) {
      console.error("❌ WBS 히스토리 추가 실패:", error);
      return false;
    }
  },

  // 👇👇 [여기에 CS 프론트 연동 API 추가] 👇👇
  // =================================================================
  // 🔧 [SECTION 14] CS 활동 관리 (Customer Service)
  // =================================================================
  getCSActivities: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/cs-activities`);
      if (!response.ok) throw new Error('CS 데이터 로드 실패');
      return await response.json();
    } catch (error) {
      console.error("❌ CS 데이터 로드 실패:", error);
      return [];
    }
  },

  saveCSActivity: async (activity: any) => {
    try {
      const response = await fetch(`${API_BASE_URL}/cs-activities/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(activity),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.detail || 'CS 활동 저장 실패');
      return json?.status === 'success';
    } catch (error) {
      console.error("❌ CS 활동 저장 실패:", error);
      return false;
    }
  },

  /**
   * 14-3. [신규] CS 활동 삭제 API
   * - App.tsx의 handleDeleteCSActivity에서 호출
   */
  deleteCSActivity: async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/cs-activities/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.detail || 'CS 활동 삭제 실패');
      return json?.status === 'success';
    } catch (error) {
      console.error("❌ CS 활동 삭제 실패:", error);
      return false;
    }
  },

  /**
   * 14-4. [신규] 매출예정표(Deal) 저장 API
   * - App.tsx의 handleConvertToDeal에서 호출 (종 모양 버튼 클릭 시)
   * - 기존 SECTION 5에 있는 save 관련 로직이 없어서 공용으로 하나 만듭니다.
   */
  saveDeal: async (dealData: any) => {
    try {
      const response = await fetch(`${API_BASE_URL}/forecast/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dealData),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.detail || '매출 정보 저장 실패');
      return json?.status === 'success';
    } catch (error) {
      console.error("❌ 매출 정보 저장 실패:", error);
      return false;
    }
  },

  /**
   * 14-5. CS 활동 데이터를 매출예정표 규격에 맞춰 저장
   */
  saveDealFromCS: async (activity: any) => {
    try {
      const now = new Date();
      const utcNow = now.getTime() + (now.getTimezoneOffset() * 60000);
      const kstDate = new Date(utcNow + (9 * 3600000)).toISOString().split('T')[0];

      // 🚨 [핵심] SalesForecast의 handleAddForecast가 보내는 규격과 100% 일치시킵니다.
      const payload = {
        project_name: `[CS연계] ${activity.company} 건`, // 백엔드: data.get('project_name')
        client_name: activity.company,                  // 백엔드: data.get('client_name')
        sales_date: kstDate,                            // 백엔드: data.get('sales_date')
        assignee: activity.salesRep || '미정',           // 백엔드: data.get('assignee')
        status: '예정',                                 // 백엔드: data.get('status')
        sales_amount: 0,                                // 백엔드: data.get('sales_amount')
        sales_amount_prod: 0,                           // 백엔드: data.get('sales_amount_prod')
        sales_amount_item: 0,                           // 백엔드: data.get('sales_amount_item')
        collection_date: '-',                           // 백엔드: data.get('collection_date')
        team: '미배정'                                   // 백엔드: data.get('team')
      };

      console.log("📡 백엔드 data.get 규격에 맞춘 데이터:", payload);

      const response = await fetch(`${API_BASE_URL}/forecast/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      const json = await response.json();
      
      // 만약 백엔드에서 "column pono does not exist"라고 하면
      // 그건 프론트 문제가 아니라 백엔드 db_row에 'poNo'라고 써있는 오타 때문입니다.
      return json?.status === 'success';
    } catch (error) {
      console.error("❌ 통신 오류:", error);
      return false;
    }
  },


  // =================================================================
  // 📡 [SECTION 15] 나라장터(G2B) 데이터 관리
  // =================================================================
  // 설명: 조달청 API를 통해 수집된 공고 리스트 및 통계 데이터를 관리한다.
  // -----------------------------------------------------------------

  /**
   * 16-1. [관리자용] 나라장터 공고 리스트 가져오기
   * - 최신 게시일 순으로 정렬된 전체 데이터를 반환받는다.
   */
  getG2BList: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/g2b/list`);
      if (!response.ok) throw new Error('나라장터 목록을 불러오는데 실패했습니다.');
      return await response.json();
    } catch (error) {
      console.error("❌ getG2BList 에러:", error);
      throw error;
    }
  },

  /**
   * 16-2. [대시보드용] 지역별 공고 분포 통계
   * - 파이 차트(Pie Chart)에 사용될 { name: '지역명', value: 건수 } 형태의 데이터를 반환한다.
   */
  getG2BStats: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/g2b/stats/region`);
      if (!response.ok) throw new Error('지역별 통계 데이터를 불러오는데 실패했습니다.');
      return await response.json();
    } catch (error) {
      console.error("❌ getG2BStats 에러:", error);
      throw error;
    }
  },

  /**
   * 16-3. [관리자용] 나라장터 데이터 개별 필드 수정 (범용)
   * @param bidNo 공고번호
   * @param field 수정할 컬럼명 (예: 'budget', 'status', 'region')
   * @param value 수정할 값
   */
  updateG2BField: async (bidNo: string, field: string, value: string | number) => {
    try {
      const formData = new FormData();
      formData.append('bid_no', bidNo);
      formData.append('field', field);
      formData.append('value', String(value));

      const response = await fetch(`${API_BASE_URL}/g2b/update-bid`, {
        method: 'PATCH',
        body: formData,
      });
      
      if (!response.ok) throw new Error('데이터 업데이트에 실패했습니다.');
      return await response.json();
    } catch (error) {
      console.error("❌ updateG2BField 에러:", error);
      throw error;
    }
  },

  deleteG2BData: async (bidNo: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/g2b/${bidNo}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) throw new Error('데이터 삭제에 실패했습니다.');
      return await response.json();
    } catch (error) {
      console.error("❌ deleteG2BData 에러:", error);
      throw error;
    }
  },

  // 🚨 [신규 추가] 16-5. [관리자용] 나라장터 공고 수동 추가 (기존 upsert 재활용)
  addG2BData: async (data: any) => {
    try {
      // 파이썬의 upsert_g2b_bid_data 함수가 이 형태를 그대로 받아서 처리합니다.
      const payload = {
        item_no: data.item_no || '-',
        bid_no: data.bid_no,
        bid_name: data.bid_name,
        instt_name: data.instt_name || '',
        region_api: data.region || '미분류', // 파이썬 DB 컬럼명에 맞춤
        status: data.status || '공고중',
        budget: Number(data.budget) || 0,
        base_price: Number(data.base_price) || 0,
        win_price: Number(data.win_price) || 0,
        win_company: data.win_company || '',
        win_rate: Number(data.win_rate) || 0,
        ntce_date: data.ntce_date ? data.ntce_date.replace('T', ' ') : null,
        end_date: data.end_date ? data.end_date.replace('T', ' ') : null,
      };

      const response = await fetch(`${API_BASE_URL}/g2b/upsert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) throw new Error('데이터 추가에 실패했습니다.');
      return await response.json();
    } catch (error) {
      console.error('❌ addG2BData 에러:', error);
      throw error;
    }
  },

  // =================================================================
  // 🛡️ [SECTION 16] 전사 권한 매트릭스 (User Permissions) 🚨 [신규 추가]
  // =================================================================

  /**
   * 16-1. 모든 직원의 권한 스위치 상태 가져오기
   */
  getAllUserPermissions: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/permissions`);
      if (!response.ok) throw new Error('권한 목록 로드 실패');
      return await response.json();
    } catch (error) {
      console.error("❌ 권한 목록 로드 오류:", error);
      return {};
    }
  },

  /**
   * 16-2. 특정 직원의 권한 스위치 업데이트
   */
  updateUserPermission: async (userId: number, updates: any) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/permissions/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.detail || '권한 업데이트 실패');
      return json;
    } catch (error) {
      console.error("❌ 권한 업데이트 오류:", error);
      return { status: "error", message: String(error) };
    }
  },

  // =================================================================
  // 📁 [SECTION 17] 거래처 첨부파일 관리 (Client File Attachments) 🚨 [신규 추가]
  // =================================================================

  /**
   * 17-1. 특정 거래처의 업로드된 파일 목록 가져오기
   */
  getClientFiles: async (clientName: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/clients/files?client_name=${encodeURIComponent(clientName)}`);
      if (!response.ok) throw new Error('파일 목록 통신 오류');
      return await response.json();
    } catch (error) {
      console.error("❌ 파일 목록 로드 실패:", error);
      return [];
    }
  },

  /**
   * 17-2. 특정 거래처에 파일 업로드하기 (드래그 앤 드롭)
   */
  uploadClientFile: async (clientName: string, file: File, editor: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('client_name', clientName);
    formData.append('editor', editor);

    try {
      const response = await fetch(`${API_BASE_URL}/clients/upload-file`, {
        method: 'POST',
        // 💡 FormData를 쓸 때는 headers에 'Content-Type'을 수동으로 넣지 않아야 브라우저가 알아서 처리합니다.
        body: formData, 
      });
      
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.detail || '파일 업로드 오류');
      return json;
    } catch (error) {
      console.error("❌ 파일 업로드 실패:", error);
      return { status: 'error', message: String(error) };
    }
  },

  /**
   * 17-3. 특정 거래처 파일 삭제하기
   */
  deleteClientFile: async (path: string) => {
    try {
      // 🚨 주소를 /api/files/client-attachment 로 변경하여 충돌 회피!
      const response = await fetch(`${API_BASE_URL}/files/client-attachment?path=${encodeURIComponent(path)}`, {
        method: 'DELETE',
      });
      
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.detail || '파일 삭제 오류');
      return json;
    } catch (error) {
      console.error("❌ 파일 삭제 실패:", error);
      return { status: 'error', message: String(error) };
    }
  }



};